import { useServices } from '@/app/providers/ServiceContext';
import { useI18n } from '@/shared/i18n';
import { ArrowRight, CheckCircle, Eye, EyeOff, Info, Lock, ShieldCheck, XCircle } from 'lucide-react';
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

export const ResetPassword = () => {
  const { userRepo, systemSettings } = useServices();
  const { t } = useI18n();

  // Get token from URL query params
  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  }, []);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [branding, setBranding] = useState({ appName: 'Hommi', primaryColor: '#8B5CF6', logoUrl: '' });

  useEffect(() => {
    if (systemSettings?.branding) {
      setBranding({
        appName: systemSettings.branding.appName || 'Hommi',
        primaryColor: systemSettings.branding.primaryColor || '#8B5CF6',
        logoUrl: systemSettings.branding.logoUrl || ''
      });
    }
  }, [systemSettings]);

  // Password validation
  const passwordValidation = useMemo(() => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
    };
  }, [password]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setError(t('resetPassword.requirements'));
      return;
    }

    if (!passwordsMatch) {
      setError(t('resetPassword.passwordMismatch'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      await userRepo.resetPassword(token, password);
      setSuccess(true);
    } catch (err: any) {
      if (err.message?.includes('inválido') || err.message?.includes('expirado') || err.message?.includes('invalid') || err.message?.includes('expired')) {
        setError(t('resetPassword.invalidToken'));
      } else {
        setError(err.message || 'Error al cambiar la contraseña');
      }
    } finally {
      setLoading(false);
    }
  };

  // If no token, show error
  if (!token) {
    return (
      <div className="min-h-screen w-full flex relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />
        <div className="w-full flex items-center justify-center relative z-10 px-6">
          <div
            className="w-full max-w-md rounded-3xl p-8 text-center"
            style={{
              background: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            }}
          >
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              {t('resetPassword.invalidToken')}
            </h2>
            <p className="text-slate-500 text-sm mb-6">
              El enlace no contiene un token válido.
            </p>
            <Link
              to="/auth/forgot-password"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                boxShadow: '0 4px 16px rgba(139, 92, 246, 0.35)',
              }}
            >
              {t('resetPassword.requestNew')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex relative overflow-hidden">
      {/* === FONDO CLARO CON MESH GRADIENT === */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />

      {/* Mesh gradient animado */}
      <div className="absolute inset-0">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-purple-300/50 rounded-full blur-[100px] float-orb" />
        <div className="absolute top-1/4 -right-32 w-[400px] h-[400px] bg-indigo-300/40 rounded-full blur-[80px] liquid-blob" />
        <div className="absolute -bottom-32 left-1/4 w-[600px] h-[600px] bg-violet-300/40 rounded-full blur-[120px] glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[350px] h-[350px] bg-pink-200/30 rounded-full blur-[80px] float-orb" style={{ animationDelay: '-3s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-200/30 rounded-full blur-[100px] liquid-blob" style={{ animationDelay: '-5s' }} />
      </div>

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* === FORMULARIO CENTRADO === */}
      <div className="w-full flex flex-col relative z-10">
        <div className="flex-1 flex items-center justify-center px-6 sm:px-8 lg:px-12 py-8">
          {/* LIQUID GLASS CARD */}
          <div
            className="w-full max-w-md rounded-3xl p-6 lg:p-8 relative"
            style={{
              background: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.08),
                0 2px 8px rgba(0, 0, 0, 0.04),
                inset 0 0 0 1px rgba(255, 255, 255, 0.5),
                inset 0 2px 0 rgba(255, 255, 255, 0.8)
              `,
            }}
          >
            {/* Top highlight */}
            <div
              className="absolute top-0 left-4 right-4 h-[2px] rounded-full"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)',
              }}
            />

            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                  boxShadow: '0 4px 16px rgba(139, 92, 246, 0.35)',
                }}
              >
                {branding.logoUrl ? (
                  <img src={branding.logoUrl} alt="Logo" className="w-6 h-6 object-contain" />
                ) : (
                  <ShieldCheck className="w-6 h-6 text-white" />
                )}
              </div>
            </div>

            {success ? (
              // Success state
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">
                  {t('resetPassword.success')}
                </h2>
                <p className="text-slate-500 text-sm mb-6">
                  {t('resetPassword.successMessage')}
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all hover:scale-[1.02]"
                  style={{
                    background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.35)',
                  }}
                >
                  {t('resetPassword.goToLogin')}
                  <ArrowRight size={16} />
                </Link>
              </div>
            ) : (
              // Form state
              <>
                {/* Header */}
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-slate-800">
                    {t('resetPassword.title')}
                  </h1>
                  <p className="mt-1 text-slate-500 text-sm">
                    {t('resetPassword.subtitle')}
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div
                      className="text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
                      style={{
                        background: 'rgba(254, 226, 226, 0.8)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(254, 202, 202, 0.8)',
                      }}
                    >
                      <Info size={16} /> {error}
                    </div>
                  )}

                  {/* New Password Input */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {t('resetPassword.newPassword')}
                    </label>
                    <div className="relative flex items-center">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                        className="w-full rounded-xl pl-10 pr-12 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-300"
                        style={{
                          background: 'rgba(255, 255, 255, 0.7)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.02)',
                        }}
                        onFocus={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                          e.target.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.15), inset 0 2px 4px rgba(0, 0, 0, 0.02)';
                        }}
                        onBlur={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.7)';
                          e.target.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                          e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.02)';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-3 flex items-center justify-center text-slate-400 hover:text-slate-600 mt-6"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Password Requirements */}
                  {password.length > 0 && (
                    <div className="space-y-1 px-1">
                      <div className={`text-xs flex items-center gap-2 ${passwordValidation.minLength ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {passwordValidation.minLength ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        Mínimo 8 caracteres
                      </div>
                      <div className={`text-xs flex items-center gap-2 ${passwordValidation.hasUppercase ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {passwordValidation.hasUppercase ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        Una letra mayúscula
                      </div>
                      <div className={`text-xs flex items-center gap-2 ${passwordValidation.hasLowercase ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {passwordValidation.hasLowercase ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        Una letra minúscula
                      </div>
                      <div className={`text-xs flex items-center gap-2 ${passwordValidation.hasNumber ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {passwordValidation.hasNumber ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        Un número
                      </div>
                    </div>
                  )}

                  {/* Confirm Password Input */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {t('resetPassword.confirmPassword')}
                    </label>
                    <div className="relative flex items-center">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        required
                        className="w-full rounded-xl pl-10 pr-12 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-300"
                        style={{
                          background: 'rgba(255, 255, 255, 0.7)',
                          backdropFilter: 'blur(10px)',
                          border: `1px solid ${confirmPassword.length > 0 ? (passwordsMatch ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)') : 'rgba(0, 0, 0, 0.08)'}`,
                          boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.02)',
                        }}
                        onFocus={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.15), inset 0 2px 4px rgba(0, 0, 0, 0.02)';
                        }}
                        onBlur={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.7)';
                          e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.02)';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-3 flex items-center justify-center text-slate-400 hover:text-slate-600"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <XCircle size={12} /> {t('resetPassword.passwordMismatch')}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || !isPasswordValid || !passwordsMatch}
                    className="w-full py-3 rounded-xl font-semibold text-sm text-white
                             flex items-center justify-center gap-2 transition-all duration-300
                             disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
                    style={{
                      background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                      boxShadow: `
                        0 4px 16px rgba(139, 92, 246, 0.35),
                        inset 0 1px 0 rgba(255, 255, 255, 0.2)
                      `,
                    }}
                  >
                    {loading ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        {t('resetPassword.submit')}
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            {/* Security badge */}
            <div className="mt-6 flex items-center justify-center gap-2 text-slate-400">
              <Lock size={12} />
              <span className="text-xs">Conexión segura</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 px-6 sm:px-8 lg:px-12 py-4">
          <p className="text-slate-400 text-xs text-center">
            <a href="#" className="hover:text-purple-600 transition-colors">Términos</a>
            {' · '}
            <a href="#" className="hover:text-purple-600 transition-colors">Privacidad</a>
          </p>
          <p className="text-slate-300 text-xs mt-1 text-center">
            Powered by <span className="text-purple-500 font-medium">Xelesta</span>
          </p>
        </div>
      </div>
    </div>
  );
};
